import React, { memo, useCallback } from 'react';
import { useFragment, graphql, useMutation } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { SectionMessageAction } from '@atlaskit/section-message';
import InformationIcon from '@atlaskit/icon/core/information';
import WarningIcon from '@atlaskit/icon/core/warning';
import { token } from '@atlaskit/tokens';
import { B500, Y500 } from '@atlaskit/theme/colors';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { COMPANY_MANAGED_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import type { ui_issueViewLayoutTemplates_DateFieldsMessage$key as DateFieldsMessageFragment } from '@atlassian/jira-relay/src/__generated__/ui_issueViewLayoutTemplates_DateFieldsMessage.graphql';
import type { ui_dateFieldsMessage_DismissActionMutation as DismissActionMutation } from '@atlassian/jira-relay/src/__generated__/ui_dateFieldsMessage_DismissActionMutation.graphql';
import getConfigurationHref from '@atlassian/jira-issue-view-foundation/src/utils/get-configuration-href.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { MessageAttributesType, Props } from './types.tsx';
import messages from './messages.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';

export const DateFieldsMessage = memo(
	({ rootRelayFragment, userPreferencesConnectionId }: Props) => {
		const { formatMessage } = useIntl();
		const cloudId = useCloudId();
		const issueKey = useIssueKey();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});

		const data = useFragment<DateFieldsMessageFragment>(
			graphql`
				fragment ui_issueViewLayoutTemplates_DateFieldsMessage on JiraQuery {
					issueByKey(cloudId: $cloudId, key: $issueKey) {
						projectField {
							project {
								projectStyle
								canAdministerProject: action(type: EDIT_PROJECT_CONFIG) {
									canPerform
								}
							}
						}
					}
				}
			`,
			rootRelayFragment?.jira || null,
		);

		const [dismiss] = useMutation<DismissActionMutation>(graphql`
			mutation ui_dateFieldsMessage_DismissActionMutation($cloudId: ID!, $issueKey: String!)
			@raw_response_type {
				jira {
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						dismissDateFieldAssociationMessageByIssueKey(issueKey: $issueKey) {
							success
							errors {
								message
							}
						}
					}
				}
			}
		`);

		const project = data?.issueByKey?.projectField?.project;
		const projectStyle = project?.projectStyle ?? COMPANY_MANAGED_PROJECT;
		const canEditProjectConfig = !!project?.canAdministerProject?.canPerform;

		const configureFieldsPageHref = getConfigurationHref(formatMessage);

		const handleDismissAction = useCallback(() => {
			fireUIAnalytics(analyticsEvent, 'dismissDateFieldsMessage', {
				isAdmin: canEditProjectConfig,
			});
			dismiss({
				variables: {
					cloudId,
					issueKey,
				},
				updater: (store, payload) => {
					if (
						payload?.jira?.userPreferences?.dismissDateFieldAssociationMessageByIssueKey
							?.success !== true
					) {
						return;
					}

					const userPreferencesStore = store.get(userPreferencesConnectionId);
					userPreferencesStore?.setValue(
						false,
						`showDateFieldAssociationMessageByIssueKey(issueKey:"${issueKey}")`,
					);
				},
				onCompleted(response) {
					const dismissResponse =
						response.jira?.userPreferences?.dismissDateFieldAssociationMessageByIssueKey;

					if (dismissResponse?.success !== true) {
						fireErrorAnalytics({
							error: new Error(
								dismissResponse?.errors?.[0]?.message ||
									'Error dismissing date field association message',
							),
							meta: {
								id: 'dismissError',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}

					// fire analytics for dismiss success
				},
				onError(error: Error) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'dismissError',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}, [
			analyticsEvent,
			canEditProjectConfig,
			dismiss,
			cloudId,
			issueKey,
			userPreferencesConnectionId,
		]);

		const enableOrConfigureFieldsAction =
			projectStyle === COMPANY_MANAGED_PROJECT ? (
				<SectionMessageAction
					key={1}
					href={configureFieldsPageHref}
					onClick={() => {
						fireUIAnalytics(analyticsEvent, 'configureDateFields');
					}}
				>
					{formatMessage(messages.configureFields)}
				</SectionMessageAction>
			) : (
				<SectionMessageAction
					key={1}
					href="#"
					onClick={() => {
						fireUIAnalytics(analyticsEvent, 'enableDateFields');
					}}
				>
					{formatMessage(messages.enable)}
				</SectionMessageAction>
			);

		const dismissAction = (
			<SectionMessageAction key={2} onClick={handleDismissAction}>
				{formatMessage(messages.dismiss)}
			</SectionMessageAction>
		);

		const messageAttributes: MessageAttributesType = canEditProjectConfig
			? {
					color: token('color.icon.warning', Y500),
					icon: WarningIcon,
					iconLabel: 'warning',
					actions: [enableOrConfigureFieldsAction, dismissAction],
					messageDescription: formatMessage(messages.enableDateFieldsDescriptionAdmin),
				}
			: {
					color: token('color.icon.information', B500),
					icon: InformationIcon,
					iconLabel: 'information',
					actions: [dismissAction],
					messageDescription: formatMessage(messages.enableDateFieldsDescriptionNonAdmin),
				};

		const Icon = messageAttributes.icon;

		return (
			<Box
				as="section"
				paddingInline="space.100"
				paddingBlock="space.200"
				testId="issue-date-fields-message.ui.date-fields-message"
			>
				<Inline space="space.100" alignBlock="stretch">
					<Box>
						<Icon
							color={messageAttributes.color}
							spacing="spacious"
							label={messageAttributes.iconLabel}
							LEGACY_size="small"
						/>
					</Box>
					<Stack space="space.100">
						<Heading as="h3" size="xsmall">
							{formatMessage(messages.enableDateFieldsTitle)}
						</Heading>

						<Box>
							<Text>{messageAttributes.messageDescription}</Text>
						</Box>

						<Inline shouldWrap separator="·" space="space.100" rowSpace="space.0" role="list">
							{messageAttributes.actions.map((action, id) => (
								<Inline role="listitem" key={id}>
									{action}
								</Inline>
							))}
						</Inline>
					</Stack>
				</Inline>
			</Box>
		);
	},
);
